import { IconInfoCircle } from "@tabler/icons-react";
import { AvisoProrrateoAutomatico } from "./AvisoProrrateoAutomatico";
import { useNavigate } from "@tanstack/react-router";
import { useProrrateo } from "@/features/prorrateo/hooks/use-prorrateo";
import { evaluarConfirmable } from "@/features/prorrateo/utils/conceptos";
import { useEsDueno } from "@/features/solicitudes/hooks/use-es-dueno";
import { useSolicitud } from "@/features/solicitudes/hooks/use-solicitud";
import { Alert, Card, Group, PageHeader, ReadOnlyBanner, Stack, Text } from "@/components/ui";
import { LayoutConStepper, PASO_PAGE_SHELL } from "@/components/ui/solicitud-stepper";
import { estaPostEnvio } from "@/lib/estado-solicitud";
import { Route } from "@/routes/_authed-tw/solicitudes/$id/prorrateo-sin-cfdi";
import { ConceptosTable } from "./ConceptosTable";
import { DispersionExcelMenu } from "./DispersionExcelMenu";
import { LineasTable } from "./LineasTable";
import { NuevaLineaManualForm } from "./NuevaLineaManualForm";
import { ProrrateoActions } from "./ProrrateoActions";
import { ProrrateoSociedadProvider } from "./ProrrateoSociedadContext";
import { formatearMonto } from "@/shared/format/money";

/**
 * Paso de prorrateo cuando no hay CFDI del SAT · lo comparten dos casos:
 *  · Contrapago (FB60): prorrateo en modo MANUAL (líneas a mano) · se confirma contra su
 *    propia suma y esa suma es lo que se concilia después contra el CFDI real.
 *  · Invoice (CA/OC · factura extranjera): prorrateo por CONCEPTOS (el total del PDF es un
 *    concepto divisible en sub-líneas) · se confirma contra el total extraído del PDF.
 */
export function ProrrateoSinCfdiPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { data: sol } = useSolicitud(id);
  const { data: prorrateo } = useProrrateo(id);

  const esDueno = useEsDueno(sol);
  const soloLectura = estaPostEnvio(sol.estado) || !esDueno;
  const confirmado = prorrateo.estado === "Confirmado";
  const esInvoice = sol.subtipo === "INVOICE";
  const esInvoiceConDocumento =
    esInvoice && (sol.tipo === "ContratoAnualizado" || sol.tipo === "OrdenDeCompra");

  // Invoice = prorrateo por conceptos (divisible); Contrapago = manual.
  const esConceptos = prorrateo.modo === "Conceptos";
  const bloqueado = confirmado || soloLectura;

  const confirmable = esConceptos
    ? evaluarConfirmable(prorrateo.lineas)
    : { ok: prorrateo.lineas.length > 0, motivo: prorrateo.lineas.length > 0 ? undefined : "Agrega al menos una línea." };

  // Invoice FB60: el indicador de sus líneas manuales lo determina el proveedor del Invoice
  // (IVA trasladado según el ajuste Invoice:ProveedoresConIvaTrasladado) · no se elige.
  // Contrapago conserva la captura manual del indicador.
  const indicadorFijo = sol.subtipo === "INVOICE";

  return (
    <ProrrateoSociedadProvider numeroSociedad={sol.organizacion?.numeroSociedad} moneda={sol.cfdi?.moneda ?? sol.invoice?.moneda ?? sol.contratoAnualizado?.moneda ?? sol.ordenCompra?.moneda}>
    <div className="w-full px-4 py-6 md:px-6" style={PASO_PAGE_SHELL}>
      <PageHeader
        title="Prorrateo"
        description={`${esInvoice ? "Invoice · factura extranjera (sin CFDI del SAT)" : "Contrapago · sin CFDI todavía"} · Estado: ${prorrateo.estado} · ${prorrateo.lineas.length} línea(s)`}
        breadcrumbs={[
          { label: "Inicio", href: "/dashboard" },
          { label: "Solicitudes", href: "/solicitudes" },
          { label: `#${sol.numeroSolicitud}`, href: `/solicitudes/${id}` },
          { label: "Prorrateo" },
        ]}
      />
      <LayoutConStepper estado={sol.estado} subtipo={sol.subtipo} tipo={sol.tipo} prorrateoAutomatico={sol.prorrateoAutomatico} paso="prorrateo" solicitudId={id} contabilizada={sol.enviadaAContabilizacion} fillHeight>
        <Stack gap="md">
          {soloLectura && <ReadOnlyBanner motivo={esDueno ? "post-envio" : "no-dueno"} mensaje="El prorrateo no puede modificarse." />}
          <AvisoProrrateoAutomatico prorrateo={prorrateo} />
          <Alert variant="info" icon={<IconInfoCircle size={16} stroke={1.7} />}>
            {esInvoice && esConceptos
              ? "El Invoice es una factura extranjera sin CFDI del SAT · su total es un concepto que puedes dividir en sub-líneas (centro de costo, cuenta e importe). Las sub-líneas deben sumar el total del PDF."
              : esInvoice
              ? `El Invoice es una factura extranjera sin CFDI del SAT · captura líneas manuales (centro de costo, cuenta, indicador e importe) que sumen exactamente el importe total del PDF${sol.invoice ? ` (${formatearMonto(sol.invoice.importeTotal, sol.invoice.moneda, { minimumFractionDigits: 2 })})` : ""}.`
              : "Contrapago no tiene CFDI todavía (llega después del pago) · captura las líneas que reflejen exactamente el total que se va a pagar. Esa suma es lo que se comparará contra el CFDI real cuando lo conciles."}
          </Alert>
          <Card padding="lg">
            <Text size="sm">
              <Text span fw={600}>
                Suma actual del prorrateo:{" "}
              </Text>
              {formatearMonto(prorrateo.sumaTotal, sol.cfdi?.moneda ?? sol.invoice?.moneda ?? sol.contratoAnualizado?.moneda ?? sol.ordenCompra?.moneda, { minimumFractionDigits: 2 })}
            </Text>
          </Card>

          {esConceptos ? (
            <>
              {!bloqueado && prorrateo.dispersionExcelHabilitada && (
                <Group justify="space-between" align="center" className="rounded-md border border-border bg-muted/30 px-3 py-2">
                  <Text size="sm" c="dimmed">
                    Puedes cargar el desglose por concepto/centro de costo desde una plantilla de Excel.
                  </Text>
                  <DispersionExcelMenu solicitudId={id} lineas={prorrateo.lineas} />
                </Group>
              )}
              <ConceptosTable lineas={prorrateo.lineas} disabled={bloqueado} solicitudId={id} numeroSolicitud={sol.numeroSolicitud} />
            </>
          ) : (
            <>
              <LineasTable lineas={prorrateo.lineas} disabled={bloqueado} solicitudId={id} numeroSolicitud={sol.numeroSolicitud} indicadorFijo={indicadorFijo} />
              {!bloqueado && <NuevaLineaManualForm solicitudId={id} indicadorFijo={indicadorFijo} />}
            </>
          )}

          <ProrrateoActions
            solicitudId={id}
            confirmado={confirmado}
            soloLectura={soloLectura}
            puedeReabrir={!prorrateo.confirmadoAutomaticamente}
            puedeConfirmar={confirmable.ok}
            motivoNoConfirmable={confirmable.motivo}
            cantidadLineas={prorrateo.lineas.length}
            labelVolver={esInvoiceConDocumento ? "Regresar a Datos generales" : "Regresar a Proveedor"}
            onVolver={() =>
              navigate({
                to: esInvoiceConDocumento ? "/solicitudes/$id/datos-generales" : "/solicitudes/$id/proveedor-manual",
                params: { id },
              })
            }
            onConfirmado={() => navigate({ to: "/solicitudes/$id/documentos", params: { id } })}
          />
        </Stack>
      </LayoutConStepper>
    </div>
    </ProrrateoSociedadProvider>
  );
}
